import React, { type FC } from "react";
import { useNavigate } from "react-router-dom";
import { useWindows } from "@app/overlay";
import { OPEN_FILTER_QUERY_PARAM } from "@app/router/queryParams";
import { CardHeader } from "@shared/ui/card-header";
import { ListItem } from "@src/components/business-list";
import { BusinessList } from "@src/components/business-list";
import { BusinessBoStateValue, BusinessDateValue, BusinessDivisionValue } from "@src/components/business-data-display";
import { useBusinessListQuery } from "@src/hooks/useBusinessListQuery";
import { useUrlFilter } from "@shared/hooks/useUrlFilter";
import { useUrlParamTrigger } from "@shared/hooks/useUrlParamTrigger";
import {
    stockActInvsServices,
    type StockActInv,
    type StockActInvExtends,
    type StockActInvFilter,
} from "../../../../api/stockActInvs";
import { stockFullInventoryListWindows } from "./stockFullInventoryList.windows";
import "./StockFullInventoryList.scss";

const FULL_INVENTORY_LIST_INCLUDES = {
    include: ["Division", "State"] satisfies Array<keyof StockActInvExtends>,
};

/** Список АИ для полной инвентаризации склада. */
export const StockFullInventoryList: FC = () => {
    const windows = useWindows();
    const navigate = useNavigate();
    const filterState = useUrlFilter<StockActInvFilter>();

    const list = useBusinessListQuery<StockActInv, StockActInvFilter>({
        queryKey: "stock.fullInventory.acts",
        filter: filterState.filter,
        queryFn: (filter, signal) =>
            stockActInvsServices.load(
                {
                    filter,
                    ...FULL_INVENTORY_LIST_INCLUDES,
                },
                { signal },
            ),
        getId: (item) => item.Code,
    });

    const openFilterWindow = (): void => {
        stockFullInventoryListWindows.filter.open(windows, {
            initialFilter: filterState.filter,
            onApply: (filter) => {
                filterState.applyFilter(filter, true);
            },
        });
    };

    useUrlParamTrigger(OPEN_FILTER_QUERY_PARAM, () => {
        openFilterWindow();
    });

    return (
        <div className="stock_full_inventory_list_page">
            <CardHeader
                text="Полная инвентаризация склада"
                buttons={[
                    {
                        type: "button",
                        text: "Поиск",
                        srcIcon: "/images/common/options.svg",
                        onClick: openFilterWindow,
                    },
                ]}
            />
            <BusinessList list={list} onOpenFilter={openFilterWindow}>
                {list.data.map((item) => (
                    <ListItem
                        key={item.Code}
                        title={`АИ №${item.Num}`}
                        props={[
                            { label: "Статус АИ", value: <BusinessBoStateValue state={item.State} /> },
                            { label: "Склад", value: <BusinessDivisionValue division={item.Division} /> },
                            { label: "Дата", value: <BusinessDateValue value={item.Date} /> },
                        ]}
                        onClick={() => {
                            navigate({ pathname: `${item.Code}` });
                        }}
                    />
                ))}
            </BusinessList>
        </div>
    );
};
